import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { SettingsCard, SettingsRow } from '../SettingsShell/SettingsPage';
import { SetInput, SetSwitch, SetTextarea } from '../SettingsShell/controls';
import { RecepAnalyzeModal } from './RecepAnalyzeModal';
import { RECEPTIONIST } from '../../settings-channels/mockData';
import type { ExtractedRule, ReceptionistData } from '../../settings-channels/types';

/** The four after-hours emergency routes (spec 44 §1). */
const AFTER_HOURS_OPTIONS: { key: ReceptionistData['afterHoursEmergency']; label: string; body: string }[] = [
  { key: 'page', label: 'Page the on-call dentist', body: 'Rings the on-call number, says who is calling and why, and connects the patient. If nobody picks up in 30 seconds, it takes a message and raises an urgent Triage task.' },
  { key: 'er', label: 'Refer to the nearest ER', body: 'Tells the patient to go to an emergency room, and raises an urgent Triage task for the morning.' },
  { key: 'morning', label: 'Call back first thing in the morning', body: 'Takes the details, tells the patient someone will call when the practice opens, and puts them at the top of Calls.' },
  { key: 'message', label: 'Take a message, marked urgent', body: 'Takes a message and raises an urgent Triage task. Promises no call time.' },
];

/** Roughly 2.5 spoken words a second — a caller hears this before they can speak. */
function spokenSeconds(text: string): number {
  return Math.round(text.trim().split(/\s+/).filter(Boolean).length / 2.5);
}

/**
 * The AI Receptionist's own four cards — greetings, what counts as an emergency,
 * how one gets booked, and house rules (spec 15 §9.5). Extracted so both the
 * (legacy) ReceptionistView and the merged AI Receptionist page (spec 39, the
 * Call channel + these, one page) render the same cards with the same state.
 * `ns` sets the four card numbers so they can sit at 01–04 alone or 04–07 in the
 * merged page.
 */
export function ReceptionistOwnCards({ ns = ['01', '02', '03', '04', '05'] }: { ns?: [string, string, string, string, string] }) {
  const [d, setD] = useState(RECEPTIONIST);
  const [analyzing, setAnalyzing] = useState<null | 'emergency' | 'house'>(null);

  const rulesFor = (which: 'emergency' | 'house') => (which === 'emergency' ? d.emergencyRules : d.houseRules);
  const setRules = (which: 'emergency' | 'house', next: ExtractedRule[]) => {
    setD((prev) => (which === 'emergency' ? { ...prev, emergencyRules: next } : { ...prev, houseRules: next }));
  };

  return (
    <>
      {/* Greetings */}
      <SettingsCard
        n={ns[0]}
        title="Greetings"
        sub={
          <>
            The first thing a patient hears.{' '}
            <strong className="text-[var(--text-secondary)]">ELVA reads it exactly as written.</strong>
          </>
        }
      >
        <GreetingRow
          label="When the practice is open"
          value={d.greetingOpen}
          onChange={(v) => { setD({ ...d, greetingOpen: v }); }}
        />
        <GreetingRow
          last
          label="When the practice is closed"
          desc={<>Say the practice is closed, when it opens, and what to do if it can&rsquo;t wait.</>}
          value={d.greetingClosed}
          onChange={(v) => { setD({ ...d, greetingClosed: v }); }}
        />
      </SettingsCard>

      {/* What counts as an emergency */}
      <SettingsCard
        n={ns[1]}
        accent="red"
        title="What counts as an emergency here"
        sub={
          <>
            Write it the way you would tell a new hire.{' '}
            <strong className="text-[var(--text-secondary)]">
              ELVA reads it and shows you what it did with each sentence
            </strong>{' '}
            — and says so when your wording could be read two ways.
          </>
        }
      >
        <AnalysedText
          label="In your own words"
          desc={<>One thing per line.</>}
          value={d.emergencyText}
          lastRead={d.emergencyLastRead}
          onChange={(v) => { setD({ ...d, emergencyText: v }); }}
          onAnalyze={() => { setAnalyzing('emergency'); }}
        />
        {d.emergencyRules.map((r) => (
          <RuleCard
            key={r.id}
            r={r}
            onToggle={(next) => {
              setRules('emergency', d.emergencyRules.map((x) => (x.id === r.id ? { ...x, enabled: next } : x)));
            }}
            onDelete={() => { setRules('emergency', d.emergencyRules.filter((x) => x.id !== r.id)); }}
          />
        ))}
      </SettingsCard>

      {/* How an emergency gets booked */}
      <SettingsCard
        n={ns[2]}
        accent="amber"
        title="How an emergency gets booked"
        sub={
          <>Three choices for booking emergencies. Each says what it costs.</>
        }
      >
        <SettingsRow
          label="Double-book an emergency into a full day"
          desc="When nothing is open, ELVA puts the patient into an occupied slot rather than offering tomorrow."
          control={
            <SetSwitch
              label="Double-book an emergency into a full day"
              checked={d.doubleBookEmergency}
              onChange={(v) => { setD({ ...d, doubleBookEmergency: v }); }}
            />
          }
        >
          <Cost>the provider runs over, and later appointments start late.</Cost>
        </SettingsRow>
        <SettingsRow
          label="Slots held back each day for emergencies"
          desc={
            <>
              Kept empty until <strong className="text-[var(--text-secondary)]">{d.heldUntil}</strong>, then released to
              ordinary booking if nobody has needed them.
            </>
          }
          control={
            <SetInput
              ariaLabel="Slots held back each day"
              mono
              align="right"
              suffix="a day"
              className="w-[118px]"
              value={d.heldSlots}
              onChange={(v) => { setD({ ...d, heldSlots: v }); }}
            />
          }
        >
          <Cost>two empty slots on a quiet day.</Cost>
        </SettingsRow>
        <SettingsRow
          last
          label="Skip insurance verification for an emergency"
          desc="ELVA books first and the front desk sorts coverage afterwards."
          control={
            <SetSwitch
              label="Skip insurance verification for an emergency"
              checked={d.skipInsuranceVerification}
              onChange={(v) => { setD({ ...d, skipInsuranceVerification: v }); }}
            />
          }
        >
          <Cost>the patient may owe more than they expected.</Cost>
        </SettingsRow>
      </SettingsCard>

      {/* When an emergency comes in after hours (spec 44 §1) */}
      <SettingsCard
        n={ns[3]}
        accent="red"
        title="When an emergency comes in after hours"
        sub="What ELVA does when the practice is closed and a caller has an emergency by your own definition above."
      >
        <div className="px-6 py-4">
          <div className="flex flex-col gap-2">
            {AFTER_HOURS_OPTIONS.map((o) => {
              const selected = d.afterHoursEmergency === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => { setD({ ...d, afterHoursEmergency: o.key }); }}
                  className={cn(
                    'flex items-start gap-3 rounded-[12px] border px-3.5 py-3 text-left transition-colors',
                    selected
                      ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]'
                      : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#ccd4e4]',
                  )}
                >
                  <span className={cn('mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border', selected ? 'border-[var(--brand-indigo)]' : 'border-[#ccd4e4]')}>
                    {selected ? <span className="h-2 w-2 rounded-full bg-[var(--brand-indigo)]" /> : null}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-semibold text-[var(--text-primary)]">{o.label}</span>
                    <span className="mt-0.5 block text-[12px] leading-[1.5] text-[var(--text-muted)]">{o.body}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {d.afterHoursEmergency === 'page' ? (
            <div className="mt-3 flex flex-wrap items-start gap-4 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-sunken)] px-4 py-3">
              <label className="min-w-[180px] flex-1">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">On-call number</span>
                <SetInput ariaLabel="On-call number" value={d.onCallNumber} onChange={(v) => { setD({ ...d, onCallNumber: v }); }} />
                {d.onCallNumber.trim() === '' ? (
                  <span className="mt-1 block text-[11.5px] font-semibold text-[var(--alert-red)]">Add the number ELVA should ring.</span>
                ) : null}
              </label>
              <label className="min-w-[180px] flex-1">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Who answers <span className="font-medium normal-case tracking-normal text-[var(--text-muted)]">(optional)</span></span>
                <SetInput ariaLabel="Who answers" value={d.onCallWho} onChange={(v) => { setD({ ...d, onCallWho: v }); }} />
              </label>
            </div>
          ) : null}

          <div className="mt-3 text-[12px] leading-[1.6] text-[var(--text-muted)]">
            A life-threatening emergency is always sent to 911, whatever is chosen here.
          </div>
        </div>
      </SettingsCard>

      {/* House rules */}
      <SettingsCard
        n={ns[4]}
        title="House rules"
        sub={
          <>
            Rules for calls, in your own words.{' '}
            <strong className="text-[var(--text-secondary)]">ELVA shows what it does with each one.</strong>
          </>
        }
      >
        <AnalysedText
          label="How should ELVA behave here?"
          desc={<>One rule per line. Greetings, fees and quiet hours have their own settings.</>}
          value={d.houseRulesText}
          lastRead={d.houseRulesLastRead}
          onChange={(v) => { setD({ ...d, houseRulesText: v }); }}
          onAnalyze={() => { setAnalyzing('house'); }}
        />
        {d.houseRules.map((r) => (
          <RuleCard
            key={r.id}
            r={r}
            onToggle={(next) => {
              setRules('house', d.houseRules.map((x) => (x.id === r.id ? { ...x, enabled: next } : x)));
            }}
            onDelete={() => { setRules('house', d.houseRules.filter((x) => x.id !== r.id)); }}
          />
        ))}
      </SettingsCard>

      <RecepAnalyzeModal
        open={analyzing !== null}
        rules={analyzing ? rulesFor(analyzing) : []}
        onClose={() => { setAnalyzing(null); }}
        onDelete={(id) => {
          if (!analyzing) return;
          setRules(analyzing, rulesFor(analyzing).filter((x) => x.id !== id));
        }}
      />
    </>
  );
}

function GreetingRow({
  label,
  desc,
  value,
  onChange,
  last,
}: {
  label: string;
  desc?: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  last?: boolean;
}) {
  return (
    <SettingsRow {...(last ? { last: true } : {})} label={label} desc={desc}>
      <div className="mt-3">
        <SetTextarea ariaLabel={label} rows={2} value={value} onChange={onChange} />
        <p className="m-0 mt-1.5 text-[11.5px] text-[var(--text-muted)]">
          <strong className="font-[var(--font-mono)] text-[var(--text-secondary)]">
            about {spokenSeconds(value)} seconds
          </strong>{' '}
          spoken.
        </p>
      </div>
    </SettingsRow>
  );
}

function AnalysedText({
  label,
  desc,
  value,
  lastRead,
  onChange,
  onAnalyze,
}: {
  label: string;
  desc: React.ReactNode;
  value: string;
  lastRead: string;
  onChange: (v: string) => void;
  onAnalyze: () => void;
}) {
  return (
    <div className="px-6 py-[18px]" style={{ borderBottom: '1px solid #edf0f6' }}>
      <div className="text-[13.5px] font-semibold text-[var(--text-primary)]">{label}</div>
      <p className="m-0 mt-1 text-[12.5px] leading-[1.6] text-[var(--text-muted)]">{desc}</p>
      <div className="mt-3">
        <SetTextarea ariaLabel={label} rows={6} value={value} onChange={onChange} />
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onAnalyze}
          className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3.5 py-2 text-[13px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
        >
          Analyze the text
        </button>
        <span className="text-[11.5px] text-[var(--text-muted)]">Last read {lastRead}</span>
      </div>
    </div>
  );
}

function RuleCard({
  r,
  onToggle,
  onDelete,
}: {
  r: ExtractedRule;
  onToggle: (next: boolean) => void;
  onDelete: () => void;
}) {
  const bar =
    r.state === 'in_force' ? 'var(--money-green)' : r.state === 'conflict' ? 'var(--warn-amber)' : 'var(--alert-red)';
  return (
    <div className="flex items-start gap-4 px-6 py-4" style={{ borderBottom: '1px solid #edf0f6' }}>
      <span aria-hidden className="mt-1 w-[3px] shrink-0 self-stretch rounded-full" style={{ background: bar }} />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-[var(--text-primary)]">&ldquo;{r.quote}&rdquo;</div>
        <p className="m-0 mt-1 text-[12.5px] leading-[1.6] text-[var(--text-muted)]">{r.effect}</p>
        {r.note ? (
          <p className="m-0 mt-2 rounded-[8px] bg-[var(--surface-sunken)] px-3 py-2 text-[12px] leading-[1.6] text-[var(--text-secondary)]">
            {r.note}
            {r.resolution ? (
              <>
                {' '}
                <em>&ldquo;{r.resolution}&rdquo;</em>
              </>
            ) : null}
          </p>
        ) : null}
      </div>
      <span className="flex shrink-0 items-center gap-2">
        {r.state === 'in_force' ? <SetSwitch label={r.quote} checked={r.enabled} onChange={onToggle} /> : null}
        <button
          type="button"
          aria-label="Delete this rule"
          onClick={onDelete}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-[9px] border border-[var(--border-default)]',
            'text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]',
          )}
        >
          <Icon kind="trash" size={13} stroke={2} />
        </button>
      </span>
    </div>
  );
}

function Cost({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 mt-2 text-[12px] leading-[1.6] text-[var(--warn-amber)]">
      <strong>What it costs:</strong> {children}
    </p>
  );
}
